import React, { useState } from 'react';
import moment from 'moment';
import classnames from 'classnames';

import {
  UTILITY_ACTIVATION_ITEM_ORDER_TYPE_FAR_OUT_MOVE_INS,
  UTILITY_MAX_ORDERS_PER_SALES_REP,
} from 'common/utility/constants';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';
import SrCopyText from 'client/components/SrCopyText';
import UPOrdersHitlistTableRowBody from 'client/components/UPOrdersHitlistTable/UPOrdersHitlistTableRowBody';

import './UPOrdersHitlistTable.less';

const getComponentValues = ({
  utilityActivation,
  currentUser,
  isSalesRep,
  orderType,
  salesRepOrdersCount,
  salesRepNonDelayedOrders,
  onChangeUtilitySalesRep,
  startOrderApiState,
}) => {
  if (!utilityActivation.salesRep?.id) {
    const hasInProgressAutomaticOrder = ['PENDING', 'IN_PROGRESS', 'AWAITING_RETRY'].includes(
      utilityActivation.automaticOrder?.automaticOrderStatus,
    );

    if (hasInProgressAutomaticOrder) {
      return {
        buttonText: 'Assistant',
        buttonDisabled: true,
        buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
      };
    }

    return {
      buttonText: 'Start',
      buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
      buttonDisabled:
        !isSalesRep ||
        salesRepNonDelayedOrders.length > 0 ||
        salesRepOrdersCount >= UTILITY_MAX_ORDERS_PER_SALES_REP ||
        orderType === UTILITY_ACTIVATION_ITEM_ORDER_TYPE_FAR_OUT_MOVE_INS ||
        startOrderApiState.inProgress,
      buttonOnClick: () => {
        onChangeUtilitySalesRep({
          utilityActivation,
          utilityActivationItem: utilityActivation.item,
          utilitySalesRepId: currentUser.id,
        });
      },
    };
  }

  if (utilityActivation.salesRep.id === currentUser.id) {
    const salesRepNonDelayedOrdersIds = salesRepNonDelayedOrders.map(({ id }) => id);
    return {
      buttonText: utilityActivation.utilityDelayedReasonId ? 'Delayed' : 'In Progress',
      buttonDisabled: salesRepNonDelayedOrders.length > 0 || startOrderApiState.inProgress,
      buttonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
      buttonOnClick: () => {
        onChangeUtilitySalesRep({
          utilityActivation,
          utilityActivationItem: utilityActivation.item,
          utilitySalesRepId: currentUser.id,
        });
      },
      canOpenBody: salesRepNonDelayedOrdersIds[0] === utilityActivation.id,
    };
  }

  return {
    buttonText: utilityActivation.salesRep.firstName,
    buttonDisabled: true,
    buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
  };
};

const UPOrdersHitlistTableRow = ({
  dispatch,
  utilityActivation,
  utilityDealers,
  currentUser,
  isSalesRep,
  isSaleRepManager,
  utilityDelayedReasons,
  orderType,
  isSortModeAccountRequestedDate,
  onReloadItem,
  onRemoveItem,
  onResetTable,
  onChangeUtilitySalesRep,
  salesRepOrdersCount,
  salesRepNonDelayedOrders,
  startOrderApiState,
}) => {
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const {
    buttonText,
    buttonDisabled,
    buttonTheme,
    buttonOnClick,
    canOpenBody,
  } = getComponentValues({
    utilityActivation,
    currentUser,
    isSalesRep,
    orderType,
    salesRepOrdersCount,
    salesRepNonDelayedOrders,
    onChangeUtilitySalesRep,
    startOrderApiState,
  });

  const newFrontMessages =
    utilityActivation.frontConversation?.lastMessageSentAt &&
    utilityActivation.frontConversation?.lastMessageReceivedAt &&
    moment(utilityActivation.frontConversation.lastMessageSentAt).isAfter(
      utilityActivation.frontConversation.lastMessageReceivedAt,
    );

  return (
    <div styleName="row">
      <div styleName="action">
        <SrButton
          type="button"
          heightType={SR_BUTTON_HEIGHT_SHORT}
          theme={buttonTheme}
          disabled={buttonDisabled}
          onClick={buttonOnClick}
        >
          {buttonText}
        </SrButton>
      </div>
      <div styleName="description">
        <div>
          <div>
            <span styleName="addressDisplay">{utilityActivation.address.display}</span>
            <SrLink
              styleName="shortId"
              to={`/utility-profit/admin/processing/${utilityActivation.shortId}`}
              target="_blank"
            >
              UPT
            </SrLink>
            {utilityActivation.frontConversation && (
              <SrLink
                styleName={classnames('frontConversation', { newFrontMessages })}
                target="_blank"
                to={`https://app.frontapp.com/open/${utilityActivation.frontConversation.id}`}
              >
                Front conversation
              </SrLink>
            )}
            {isSaleRepManager &&
              utilityActivation.salesRep.id &&
              utilityActivation.salesRep.id !== currentUser.id && (
                <span
                  styleName="orderDetailsToggle"
                  onClick={() => setIsOrderDetailsOpen(!isOrderDetailsOpen)}
                >
                  {isOrderDetailsOpen ? 'Hide' : 'Show'} details
                </span>
              )}
          </div>
          <div styleName="lineDetails">
            <div styleName="userName">
              <SrCopyText
                text={`${utilityActivation.user.firstName} ${utilityActivation.user.lastName}`}
                className="orderHitlistTable"
              />
            </div>
            <div styleName="userPhone">
              <SrCopyText text={utilityActivation.user.phone} className="orderHitlistTable" />
            </div>
            <div styleName="userEmail">
              <SrCopyText text={utilityActivation.user.email} />
            </div>
            <span styleName="leaseStartDate">
              Move-in:{' '}
              {utilityActivation.leaseStartDate
                ? moment(utilityActivation.leaseStartDate).format('MMM D YYYY')
                : '--'}
            </span>
            <span styleName="providerName">
              {`${utilityActivation.provider.title} `}(
              {utilityActivation.provider.requestPurchaseBeforeMoveInDays} days)
            </span>
            {utilityActivation.item?.agreeToTermsAndConditions && (
              <span styleName="termsAgreed">Terms agreed!</span>
            )}
            {utilityActivation.item?.isTransferToPurchase && (
              <span styleName="isTransfer">Is Transfer</span>
            )}
            {utilityActivation.utilityDelayedReasonId && (
              <span styleName="delayedReason">
                {
                  utilityDelayedReasons.find(
                    ({ id }) => id === utilityActivation.utilityDelayedReasonId,
                  ).reason
                }
              </span>
            )}
          </div>
          <div styleName="lineDetails">
            <span styleName="leaseStartDate">{utilityActivation.partner.name}</span>
            <span
              styleName={classnames('leaseStartDate', { bold: isSortModeAccountRequestedDate })}
            >
              Requested:{' '}
              {utilityActivation.accountRequestedAt
                ? moment(utilityActivation.accountRequestedAt).format('MMM D YYYY')
                : '--'}
            </span>
          </div>
        </div>
      </div>
      {(canOpenBody || isOrderDetailsOpen) && (
        <div styleName="body">
          <UPOrdersHitlistTableRowBody
            dispatch={dispatch}
            currentUser={currentUser}
            orderType={orderType}
            utilityActivationShortId={utilityActivation.shortId}
            utilityActivationId={utilityActivation.id}
            utilityActivationItem={utilityActivation.item}
            automaticOrder={utilityActivation.automaticOrder || []}
            utilityDealers={utilityDealers}
            utilityDelayedReasons={utilityDelayedReasons}
            onReloadItem={onReloadItem}
            onRemoveItem={onRemoveItem}
            onResetTable={onResetTable}
          />
        </div>
      )}
    </div>
  );
};

UPOrdersHitlistTableRow.displayName = 'UPOrdersHitlistTableRow';

export default UPOrdersHitlistTableRow;
